import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RxSetItemInput } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { draftFromSetItem, isNamed, type PrescriptionDraft } from './prescription-draft';

interface RxSetRow {
  id: string;
  name: string;
  items: Partial<RxSetItemInput>[];
}

export function draftToSetItem(draft: PrescriptionDraft): RxSetItemInput {
  return {
    productId: draft.productId,
    drugName: draft.drugName.trim(),
    strength: draft.strength.trim() || undefined,
    form: draft.form.trim() || undefined,
    dose: draft.dose.trim() || '1',
    ...(draft.route ? { route: draft.route } : {}),
    frequency: draft.frequency.trim() || 'OD',
    durationDays: draft.durationDays,
    quantity: draft.quantity,
    instructions: draft.instructions.trim() || undefined,
    isSos: draft.isSos,
  };
}

export function RxSetsMenu({ items, onLoad }: { items: PrescriptionDraft[]; onLoad: (drafts: PrescriptionDraft[]) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const named = items.filter(isNamed);

  const sets = useQuery({ queryKey: ['rx-sets'], queryFn: () => get<RxSetRow[]>('/rx-sets'), staleTime: 60_000 });

  const save = useMutation({
    mutationFn: () => post('/rx-sets', { name: name.trim(), items: named.map(draftToSetItem) }),
    onSuccess: () => {
      toast.success(tr('opd', 'set_saved'));
      setSaving(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['rx-sets'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <>
      <Select
        value=""
        className="h-8 w-40"
        aria-label={tr('opd', 'load_a_prescription_set')}
        onChange={(e) => {
          const chosen = sets.data?.find((set) => set.id === e.target.value);
          if (chosen) onLoad(chosen.items.map(draftFromSetItem));
        }}
      >
        <option value="">{tr('opd', 'load_set')}</option>
        {sets.data?.map((set) => (
          <option key={set.id} value={set.id}>
            {set.name}
          </option>
        ))}
      </Select>
      <Button variant="ghost" size="sm" onClick={() => setSaving(true)} disabled={named.length === 0}>
        {tr('opd', 'save_as_set')}
      </Button>
      {saving && (
        <Dialog
          open
          onClose={() => setSaving(false)}
          title={tr('opd', 'save_as_a_prescription_set')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setSaving(false)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button onClick={() => save.mutate()} loading={save.isPending} disabled={name.trim().length < 2}>
                {tr('opd', 'save')}
              </Button>
            </>
          }
        >
          <Field label={tr('opd', 'name')} required>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          {error && (
            <Alert variant="destructive" className="mt-3">
              {error}
            </Alert>
          )}
        </Dialog>
      )}
    </>
  );
}
